import React, { useEffect, useState } from "react";
import {  RCCButton, RCCGrid, RCCInput, RCCTextArea, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import Accordion from "src/components/Accordion";
import LovUsers from "src/components/Lov/Users";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { IComponentContingencePlansItem, IItemContingencePlan } from "./interface";

const ContingencePlanItem: React.FC<IComponentContingencePlansItem> = ({
	control,
	defaultValue,
	error,
	index,
	disabled = false,
	clearErrors,
	register
}) => {
	const { translate } = useTranslation();
	const { user } = useAuth();

	const [disableLastItem, setDisableLastItem] = useState(false);
	
	const { 
		fields,
		remove,
		append	
	} = useFieldArray({
		control,
		name: `ritbb005[${index}].ritbb006`,
		shouldUnregister: true
	});

	useEffect(() => {
		if(defaultValue){
			append(defaultValue);
			return;
		}

		const newField = {} as IItemContingencePlan;
		newField.id = v4();
		append(newField);
	}, []);


	const apeendItem = () => {
		clearErrors(`ritbb005[${index}].ritbb006`);
		const newField = {} as IItemContingencePlan;
		newField.id = v4();
		append(newField);
	};

	useEffect(() => {
		if(fields.length <= 1){
			setDisableLastItem(true);
			return;
		}
		setDisableLastItem(false);
	}, [fields]);
	

	return (
		<>
			<Accordion
				header={
					<RCCGrid
						container
						item
						justifyContent="space-between"
					>	
						<RCCGrid
							item
							container
						>
							<RCCGrid
								item
								container
								xs={9}
							>
								<Title color="purple" size="md">
									{translate("Ações")}
								</Title>
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={3}
								justifyContent="flex-end"
							>
								<Title color="purple" size="sm" styleInline="margin 0 10px;">{translate("Total")}: {fields.length ?? 0}</Title>
								{!disabled && (
									<RCCTooltip
										color="purple"
										text={translate("Adicionar plano de ação")}				
									>
										<RCCButton
											color="purple"
											outlined
											icon={{
												name: "plus",
												size: 12
											}}
											onClick={(e) => { e.stopPropagation(); apeendItem(); }}
											size="xs"
											layout="isRounded"
											disabled={disabled}
										/>
									</RCCTooltip>
								)}
							</RCCGrid>	
						</RCCGrid>
					</RCCGrid>
				}
				styleInline={{
					header:"background:var(--header-color); border: 1px solid var(--border-color);"
				}}
			>
				<RCCGrid
					container
					item
				>
					<RCCGrid 
						item
						container
						xs={12}
					>
						{fields.map((action: any, idx: number) => (
							<RCCGrid 
								item
								container
								xs={12}
								direction="column"
								alignItems="center"
								key={action.id}
								style={{ marginBottom: "20px" }}
							>
								<RCCGrid 
									item
									container
									xs={12}
									md={11}
								>
									{idx >= 1 && (
										<hr />
									)}
									<RCCGrid 
										item
										container
										xs={12}
										md={12}
										alignItems="center"
										justifyContent="space-between"
									>
										<RCCGrid 
											item
											container
											xs={ disabled ? 12: 11}
											alignItems="center"
											justifyContent="center"
										>
											<RCCInput 
												name={`ritbb005[${index}].ritbb006[${idx}].rib006Titulo`}
												label={translate("Título")}
												maxLength={60}
												register={register}
												defaultValue={action.rib006Titulo ?? ""}
												error={error.ritbb006 && error.ritbb006[idx]?.rib006Titulo}
												required
												disabled={disabled}
											/>
										</RCCGrid>
										{!disabled && (
											<RCCGrid 
												item
												container
												xs={1}
												alignItems="center"
												justifyContent="flex-end"
												style={{ marginTop: "15px" }}
											>
												<RCCTooltip
													color="red"
													text={translate("Remover ação")}			
												>
													<RCCButton
														color="red"
														icon={{
															name: "trash",
															size: 12
														}}
														size="xs"	
														layout="isRounded"
														onClick={() => remove(idx)}
														disabled={disableLastItem || disabled}
													/>
												</RCCTooltip>
											</RCCGrid>
										)}
									</RCCGrid>
									<RCCGrid 
										item
										container
										xs={12}
										md={6}
										styleInline="@media (min-width: 600px){padding-right: 4px;}"
									>
										<RCCInput 
											name={`ritbb005[${index}].ritbb006[${idx}].rib006Dias`}
											label={`${translate("Dias para ação ser concluída")}`}
											maxLength={100}
											register={register}
											defaultValue={action.rib006Dias ?? 0}
											type="number"
											min={0}
											max={99999}
											error={error.ritbb006 && error.ritbb006[idx]?.rib006Dias}
											disabled={disabled}
											required
										/>
									</RCCGrid>
									<RCCGrid 
										item
										container
										xs={12}
										md={6}
										styleInline="@media (min-width: 600px){padding-left: 4px;}"
									>
										<LovUsers 
											control={control}
											name={`ritbb005[${index}].ritbb006[${idx}].rib006Efa005Id`}
											label={translate("Responsável")}
											defaultValue={{
												value: action.rib006Efa005Id ?? `${user.efa005Id}`, 
												label: action.rib006Efa005Nome ?? `${user.efa005Nome}`
											}}
											error={error.ritbb006 && error.ritbb006[idx]?.rib006Efa005Id}
											disabled={disabled}
											required
										/>
									</RCCGrid>
									<RCCGrid container item>
										<RCCTextArea 
											name={`ritbb005[${index}].ritbb006[${idx}].rib006Como`}
											label={`${translate("Como")} ?`}
											maxLength={4000}	
											register={register}
											defaultValue={action.rib006Como ?? ""}
											error={error.ritbb006 && error.ritbb006[idx]?.rib006Como}
											disabled={disabled}
											required
										/>
									</RCCGrid>
								</RCCGrid>	
							</RCCGrid>
						))}
					</RCCGrid>
				</RCCGrid>
			</Accordion>	
		</>
	);
};

export default ContingencePlanItem;
